import { Button, Tooltip } from 'src/shared/components/ui'
import { Heart } from 'lucide-react'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { cn } from 'src/shared/components/ui/utils'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import {
  useOrgFavouriteCheckApi,
  useToggleOrgFavouriteApi,
} from 'src/api/db/apiOrgFavourites'

export const FavouriteProductButton = ({
  productId,
  variant = 'mobile',
  orgIdOverride,
}: {
  productId: number
  variant?: 'mobile' | 'desktop'
  /** When set (e.g. super admin viewing another org’s favourites), favourites use this org instead of the signed-in user’s org. */
  orgIdOverride?: string | null
}) => {
  const { t } = useLocalization()
  const { orgUser } = useOrgUsersApi()
  const orgIdFromUser = orgUser?.data?.organization_id ?? null
  const orgId =
    orgIdOverride !== undefined ? orgIdOverride || null : orgIdFromUser
  const { favouritedIds } = useOrgFavouriteCheckApi({
    orgId,
    productIds: [productId],
  })
  const { toggleFavourite } = useToggleOrgFavouriteApi()

  const isFavourited = favouritedIds.has(productId)
  const hasOrg = !!orgId

  const handleClick = () => {
    if (!orgId) return
    toggleFavourite.mutate({
      orgId,
      productId,
      isFavourited,
    })
  }

  const gap = variant === 'mobile' ? 'gap-1' : 'gap-2'
  const width = variant === 'desktop' ? 'w-fit' : ''

  const button = (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      disabled={toggleFavourite.isPending || !hasOrg}
      className={cn('flex items-center', gap, width, isFavourited && 'text-primary')}
    >
      <Heart className={cn('w-4 h-4', isFavourited && 'fill-current')} />
      {t(
        isFavourited ? 'Remove from favorites' : 'Add to favorites',
        isFavourited ? 'Ta bort från favoriter' : 'Lägg till i favoriter'
      )}
    </Button>
  )

  if (!hasOrg) {
    return (
      <Tooltip
        content={t(
          'Select an organization to save favourites',
          'Välj en organisation för att spara favoriter'
        )}
      >
        {button}
      </Tooltip>
    )
  }

  return button
}
